import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Award, TrendingUp, CheckCircle2 } from 'lucide-react';

/**
 * Member-facing $500-milestone progress ring shown on /stocks. Calls
 * `/api/stocks/eligibility` to get the latest progress (also triggers a
 * server-side re-evaluation as a side effect).
 *
 * Visual: SVG donut where each $500 chunk is a coloured segment. Gold for
 * earned, faint cyan for the chunk currently in progress (with a glowing
 * tip), and grey for chunks still locked behind the tier cap. Center stat
 * shows the dollar amount needed for the NEXT chunk.
 */
export function MilestoneProgressRing() {
  const { api } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/api/stocks/eligibility')
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [api]);

  if (!data) {
    return (
      <div className="rounded-2xl border border-white/10 bg-[#041A3B] p-5 h-[230px] animate-pulse" />
    );
  }

  const chunkSize = data.chunk_size_usd || 500;
  const progressPerChunk = data.progress_per_chunk_usd || (chunkSize * 2);
  const maxChunks = Math.max(1, data.max_chunks || 0);
  const earned = Math.min(maxChunks, data.earned_chunks || 0);
  const totalUsd = Math.max(0, data.total_usd || 0);
  const tierCap = data.tier_cap || 0;
  const isMaxed = earned >= maxChunks && maxChunks > 0;
  const progressIntoCurrent = isMaxed ? 0 : totalUsd - earned * progressPerChunk;
  const toNext = isMaxed ? 0 : Math.max(0, progressPerChunk - progressIntoCurrent);

  // Ring geometry — single donut split into `maxChunks` segments.
  const size = 180;
  const stroke = 14;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const gap = 4; // px between segments
  const segLen = (circumference - gap * maxChunks) / maxChunks;

  const segments = [];
  for (let i = 0; i < maxChunks; i++) {
    const offset = -i * (segLen + gap);
    const isEarned = i < earned;
    const isCurrent = i === earned && !isMaxed;
    let color = 'rgba(255,255,255,0.06)';
    if (isEarned) color = '#FFEA00';
    else if (isCurrent) color = '#00E5FF';
    let dasharray;
    if (isCurrent) {
      const pctOfChunk = progressIntoCurrent / progressPerChunk;
      const filled = segLen * pctOfChunk;
      dasharray = `${filled} ${circumference}`;
    } else {
      dasharray = `${segLen} ${circumference}`;
    }
    segments.push(
      <circle
        key={i}
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="transparent"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={dasharray}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={isCurrent ? { filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.8))' } : undefined}
      />
    );
    // For non-current chunks, also render the "track" underneath using a
    // muted stroke so the segment outline is visible when nothing's earned.
    if (!isEarned && !isCurrent) {
      segments.push(
        <circle
          key={`bg-${i}`}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${segLen} ${circumference}`}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      );
    }
  }

  return (
    <div
      className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#041A3B] to-[#000B18] p-5 flex flex-col sm:flex-row items-center gap-5"
      data-testid="milestone-progress-ring"
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="overflow-visible">
          {segments}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
          {isMaxed ? (
            <>
              <CheckCircle2 className="w-7 h-7 text-[#FFEA00] mb-1" />
              <span className="font-outfit text-base font-semibold text-white">Maxed out</span>
              <span className="text-[10px] text-[#A0B4CC]">${tierCap.toLocaleString()} cert</span>
            </>
          ) : (
            <>
              <span className="text-[10px] uppercase tracking-wider text-[#A0B4CC]">Next chunk</span>
              <span className="font-outfit text-2xl font-semibold text-white tabular-nums">
                ${toNext.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
              <span className="text-[10px] text-[#5C7599]">to go</span>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <Award className="w-4 h-4 text-[#FFEA00]" />
          <p className="text-white font-outfit font-semibold">Stock Certificate Progress</p>
        </div>
        <p className="text-sm text-[#A0B4CC] leading-relaxed">
          {isMaxed
            ? <>You&apos;ve earned all <b className="text-[#FFEA00]">{maxChunks}</b> ${chunkSize} certificate{maxChunks === 1 ? '' : 's'} available for your tier — ${tierCap.toLocaleString()} total. Refer more members or upgrade your plan to unlock additional caps.</>
            : <>You&apos;ve earned <b className="text-[#FFEA00]">{earned}</b> of <b>{maxChunks}</b> ${chunkSize} certificates so far. Each <b className="text-[#00E5FF]">${progressPerChunk.toLocaleString()}</b> of combined subscription value (yours + Level 1 + Level 2 referrals) unlocks the next ${chunkSize} certificate (2:1 ratio).</>}
        </p>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <Tile label="Earned" value={`$${(earned * chunkSize).toLocaleString()}`} color="#FFEA00" />
          <Tile label="Progress" value={`$${Number(totalUsd).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} color="#00E5FF" />
          <Tile label="Tier cap" value={`$${tierCap.toLocaleString()}`} color="#A0B4CC" />
        </div>
        {!isMaxed && (
          <p className="mt-3 text-[11px] text-[#5C7599] flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            Tip: invite more members and watch the ring fill up.
          </p>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, color }) {
  return (
    <div className="rounded-lg border border-white/5 bg-[#000B18]/60 p-2">
      <p className="text-[9px] uppercase tracking-wider text-[#5C7599]">{label}</p>
      <p className="font-outfit text-sm font-semibold tabular-nums mt-0.5" style={{ color }}>{value}</p>
    </div>
  );
}

export default MilestoneProgressRing;
